import * as questionnaires from '@seen/core/models/questionnaires/index'
import { ValidatedTool } from '@seen/core/models/validatedTool'
import type { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import React, { type JSX } from 'react'
import { z } from 'zod'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormSubmitButton } from '#src/components/core/form/FormSubmitButton.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useValidatedToolForm } from '#src/components/validated-tool-form/shared/useValidatedToolForm.ts'
import {
  ValidatedToolInnerForm,
  type ValidatedToolInnerFormProps,
} from '#src/components/validated-tool-form/shared/ValidatedToolInnerForm.tsx'
import { ValidatedToolResponseCard } from '#src/components/validated-tool-form/shared/ValidatedToolResponseCard.tsx'
import { ValidatedToolSection } from '#src/components/validated-tool-form/shared/ValidatedToolSection.tsx'
import type { UseFormReturn } from '#src/hooks/useForm.ts'

export interface COPMFormProps extends Omit<
  ValidatedToolInnerFormProps,
  'validatedTool' | 'form' | 'subtitle'
> {
  response?: ValidatedToolResponse
  restorationId?: string
}

const importancePerformanceSatisfactionSchema = z
  .number()
  .min(0)
  .max(10)
  .int()
  .nullable()

const schema = z
  .object({
    'self-care-personal-care-0': z.string().nullable(),
    'self-care-personal-care-0-importance':
      importancePerformanceSatisfactionSchema,
    'self-care-personal-care-1': z.string().nullable(),
    'self-care-personal-care-1-importance':
      importancePerformanceSatisfactionSchema,
    'self-care-personal-care-2': z.string().nullable(),
    'self-care-personal-care-2-importance':
      importancePerformanceSatisfactionSchema,
    'self-care-personal-care-3': z.string().nullable(),
    'self-care-personal-care-3-importance':
      importancePerformanceSatisfactionSchema,
    'self-care-personal-care-4': z.string().nullable(),
    'self-care-personal-care-4-importance':
      importancePerformanceSatisfactionSchema,
    'self-care-functional-mobility-0': z.string().nullable(),
    'self-care-functional-mobility-0-importance':
      importancePerformanceSatisfactionSchema,
    'self-care-functional-mobility-1': z.string().nullable(),
    'self-care-functional-mobility-1-importance':
      importancePerformanceSatisfactionSchema,
    'self-care-functional-mobility-2': z.string().nullable(),
    'self-care-functional-mobility-2-importance':
      importancePerformanceSatisfactionSchema,
    'self-care-functional-mobility-3': z.string().nullable(),
    'self-care-functional-mobility-3-importance':
      importancePerformanceSatisfactionSchema,
    'self-care-functional-mobility-4': z.string().nullable(),
    'self-care-functional-mobility-4-importance':
      importancePerformanceSatisfactionSchema,
    'self-care-community-management-0': z.string().nullable(),
    'self-care-community-management-0-importance':
      importancePerformanceSatisfactionSchema,
    'self-care-community-management-1': z.string().nullable(),
    'self-care-community-management-1-importance':
      importancePerformanceSatisfactionSchema,
    'self-care-community-management-2': z.string().nullable(),
    'self-care-community-management-2-importance':
      importancePerformanceSatisfactionSchema,
    'self-care-community-management-3': z.string().nullable(),
    'self-care-community-management-3-importance':
      importancePerformanceSatisfactionSchema,
    'self-care-community-management-4': z.string().nullable(),
    'self-care-community-management-4-importance':
      importancePerformanceSatisfactionSchema,
    'productivity-paid-unpaid-work-0': z.string().nullable(),
    'productivity-paid-unpaid-work-0-importance':
      importancePerformanceSatisfactionSchema,
    'productivity-paid-unpaid-work-1': z.string().nullable(),
    'productivity-paid-unpaid-work-1-importance':
      importancePerformanceSatisfactionSchema,
    'productivity-paid-unpaid-work-2': z.string().nullable(),
    'productivity-paid-unpaid-work-2-importance':
      importancePerformanceSatisfactionSchema,
    'productivity-paid-unpaid-work-3': z.string().nullable(),
    'productivity-paid-unpaid-work-3-importance':
      importancePerformanceSatisfactionSchema,
    'productivity-paid-unpaid-work-4': z.string().nullable(),
    'productivity-paid-unpaid-work-4-importance':
      importancePerformanceSatisfactionSchema,
    'productivity-household-management-0': z.string().nullable(),
    'productivity-household-management-0-importance':
      importancePerformanceSatisfactionSchema,
    'productivity-household-management-1': z.string().nullable(),
    'productivity-household-management-1-importance':
      importancePerformanceSatisfactionSchema,
    'productivity-household-management-2': z.string().nullable(),
    'productivity-household-management-2-importance':
      importancePerformanceSatisfactionSchema,
    'productivity-household-management-3': z.string().nullable(),
    'productivity-household-management-3-importance':
      importancePerformanceSatisfactionSchema,
    'productivity-household-management-4': z.string().nullable(),
    'productivity-household-management-4-importance':
      importancePerformanceSatisfactionSchema,
    'productivity-play-school-0': z.string().nullable(),
    'productivity-play-school-0-importance':
      importancePerformanceSatisfactionSchema,
    'productivity-play-school-1': z.string().nullable(),
    'productivity-play-school-1-importance':
      importancePerformanceSatisfactionSchema,
    'productivity-play-school-2': z.string().nullable(),
    'productivity-play-school-2-importance':
      importancePerformanceSatisfactionSchema,
    'productivity-play-school-3': z.string().nullable(),
    'productivity-play-school-3-importance':
      importancePerformanceSatisfactionSchema,
    'productivity-play-school-4': z.string().nullable(),
    'productivity-play-school-4-importance':
      importancePerformanceSatisfactionSchema,
    'leisure-quiet-recreation-0': z.string().nullable(),
    'leisure-quiet-recreation-0-importance':
      importancePerformanceSatisfactionSchema,
    'leisure-quiet-recreation-1': z.string().nullable(),
    'leisure-quiet-recreation-1-importance':
      importancePerformanceSatisfactionSchema,
    'leisure-quiet-recreation-2': z.string().nullable(),
    'leisure-quiet-recreation-2-importance':
      importancePerformanceSatisfactionSchema,
    'leisure-quiet-recreation-3': z.string().nullable(),
    'leisure-quiet-recreation-3-importance':
      importancePerformanceSatisfactionSchema,
    'leisure-quiet-recreation-4': z.string().nullable(),
    'leisure-quiet-recreation-4-importance':
      importancePerformanceSatisfactionSchema,
    'leisure-active-recreation-0': z.string().nullable(),
    'leisure-active-recreation-0-importance':
      importancePerformanceSatisfactionSchema,
    'leisure-active-recreation-1': z.string().nullable(),
    'leisure-active-recreation-1-importance':
      importancePerformanceSatisfactionSchema,
    'leisure-active-recreation-2': z.string().nullable(),
    'leisure-active-recreation-2-importance':
      importancePerformanceSatisfactionSchema,
    'leisure-active-recreation-3': z.string().nullable(),
    'leisure-active-recreation-3-importance':
      importancePerformanceSatisfactionSchema,
    'leisure-active-recreation-4': z.string().nullable(),
    'leisure-active-recreation-4-importance':
      importancePerformanceSatisfactionSchema,
    'leisure-socialization-0': z.string().nullable(),
    'leisure-socialization-0-importance':
      importancePerformanceSatisfactionSchema,
    'leisure-socialization-1': z.string().nullable(),
    'leisure-socialization-1-importance':
      importancePerformanceSatisfactionSchema,
    'leisure-socialization-2': z.string().nullable(),
    'leisure-socialization-2-importance':
      importancePerformanceSatisfactionSchema,
    'leisure-socialization-3': z.string().nullable(),
    'leisure-socialization-3-importance':
      importancePerformanceSatisfactionSchema,
    'leisure-socialization-4': z.string().nullable(),
    'leisure-socialization-4-importance':
      importancePerformanceSatisfactionSchema,
    'occupational-performance-problems-0': z.string().nullable(),
    'occupational-performance-problems-0-performance':
      importancePerformanceSatisfactionSchema,
    'occupational-performance-problems-0-satisfaction':
      importancePerformanceSatisfactionSchema,
    'occupational-performance-problems-1': z.string().nullable(),
    'occupational-performance-problems-1-performance':
      importancePerformanceSatisfactionSchema,
    'occupational-performance-problems-1-satisfaction':
      importancePerformanceSatisfactionSchema,
    'occupational-performance-problems-2': z.string().nullable(),
    'occupational-performance-problems-2-performance':
      importancePerformanceSatisfactionSchema,
    'occupational-performance-problems-2-satisfaction':
      importancePerformanceSatisfactionSchema,
    'occupational-performance-problems-3': z.string().nullable(),
    'occupational-performance-problems-3-performance':
      importancePerformanceSatisfactionSchema,
    'occupational-performance-problems-3-satisfaction':
      importancePerformanceSatisfactionSchema,
    'occupational-performance-problems-4': z.string().nullable(),
    'occupational-performance-problems-4-performance':
      importancePerformanceSatisfactionSchema,
    'occupational-performance-problems-4-satisfaction':
      importancePerformanceSatisfactionSchema,
    summary: z.string().nullable(),
  })
  .superRefine((data, ctx) => {
    Object.entries(data).forEach(([key, value]) => {
      // requires that text is set if importance is set
      if (key.endsWith('-importance')) {
        const itemKey = key.replace('-importance', '')
        if (value !== null && !data[itemKey as keyof typeof data]) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Required',
            path: [itemKey],
          })
        }
      }
    })
  })

const validatedTool = new ValidatedTool(questionnaires.copmQuestionnaire)

export function COPMForm({
  response,
  restorationId,
  ...delegated
}: COPMFormProps): JSX.Element {
  const form = useValidatedToolForm({
    schema,
    validatedTool,
    response,
    restorationId,
  })

  return (
    <ValidatedToolInnerForm
      validatedTool={validatedTool}
      subtitle="For detecting self-perceived occupational performance problems over time"
      form={form}
      response={response}
      {...delegated}
    >
      {!!response && (
        <ValidatedToolResponseCard response={response}>
          <Score
            form={form}
            validatedTool={validatedTool}
            response={response}
          />
          <FormField
            control={form.control}
            name="summary"
            render={({ field: { value, ...rest } }) =>
              !value ? null : (
                <RichTextEditor
                  defaultValue={value}
                  variant="unstyled"
                  placeholder="Add summary…"
                  contentClassName="max-w-(--container-prose)"
                  {...rest}
                  disabled={false}
                  editable={false}
                />
              )
            }
          />
        </ValidatedToolResponseCard>
      )}
      <ValidatedToolSection
        title="Self-Care"
        subtitle="Personal care and self-care activities"
      >
        <div className="max-w-lg pl-4">
          {validatedTool.getItem('self-care')?.item?.map((item) => (
            <div key={item.linkId} className="pt-4">
              <p className="text-sm font-medium">{item.text}</p>
              <p className="text-sm text-muted-foreground">{item.prefix}</p>
              <div className="mt-2 grid grid-cols-3 gap-2">
                <ThingWithImportance
                  control={form.control}
                  linkId={item.linkId}
                  index={0}
                />
                <ThingWithImportance
                  control={form.control}
                  linkId={item.linkId}
                  index={1}
                />
                <ThingWithImportance
                  control={form.control}
                  linkId={item.linkId}
                  index={2}
                />
              </div>
            </div>
          ))}
        </div>
      </ValidatedToolSection>
      <ValidatedToolSection
        title="Productivity"
        subtitle="Paid and unpaid work, household management, play and school"
      >
        <div className="max-w-lg pl-4">
          {validatedTool.getItem('productivity')?.item?.map((item) => (
            <div key={item.linkId} className="pt-4">
              <p className="text-sm font-medium">{item.text}</p>
              <p className="text-sm text-muted-foreground">{item.prefix}</p>
              <div className="mt-2 grid grid-cols-3 gap-2">
                <ThingWithImportance
                  control={form.control}
                  linkId={item.linkId}
                  index={0}
                />
                <ThingWithImportance
                  control={form.control}
                  linkId={item.linkId}
                  index={1}
                />
                <ThingWithImportance
                  control={form.control}
                  linkId={item.linkId}
                  index={2}
                />
              </div>
            </div>
          ))}
        </div>
      </ValidatedToolSection>
      <ValidatedToolSection
        title="Leisure"
        subtitle="Quiet and active recreation, socialization"
      >
        <div className="max-w-lg pl-4">
          {validatedTool.getItem('leisure')?.item?.map((item) => (
            <div key={item.linkId} className="pt-4">
              <p className="text-sm font-medium">{item.text}</p>
              <p className="text-sm text-muted-foreground">{item.prefix}</p>
              <div className="mt-2 grid grid-cols-3 gap-2">
                <ThingWithImportance
                  control={form.control}
                  linkId={item.linkId}
                  index={0}
                />
                <ThingWithImportance
                  control={form.control}
                  linkId={item.linkId}
                  index={1}
                />
                <ThingWithImportance
                  control={form.control}
                  linkId={item.linkId}
                  index={2}
                />
              </div>
            </div>
          ))}
        </div>
      </ValidatedToolSection>
      <ValidatedToolSection title="Occupational Performance Problems">
        <div className="max-w-2xl pl-4">
          <div className="mt-2 grid grid-cols-8 gap-2">
            {new Array(questionnaires.COPM_MAX_ITEMS_PER_SECTION)
              .fill(null)
              .map((_, idx) => (
                <React.Fragment key={idx}>
                  <FormField
                    control={form.control}
                    name={`occupational-performance-problems-${idx}` as any}
                    render={({ field }) => (
                      <RichTextEditor
                        variant="underline"
                        placeholder={`Thing ${idx + 1}…`}
                        contentClassName="max-w-(--container-prose)"
                        className="col-span-4"
                        {...field}
                      />
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={
                      `occupational-performance-problems-${idx}-performance` as any
                    }
                    render={({ field: { onChange, ...rest } }) => (
                      <Input
                        type="number"
                        placeholder="Performance (1-10)"
                        className="col-span-2"
                        min={1}
                        max={10}
                        onChange={(e) => {
                          onChange(Number(e.target.value))
                        }}
                        {...rest}
                      />
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={
                      `occupational-performance-problems-${idx}-satisfaction` as any
                    }
                    render={({ field: { onChange, ...rest } }) => (
                      <Input
                        type="number"
                        placeholder="Satisfaction (1-10)"
                        className="col-span-2"
                        min={1}
                        max={10}
                        onChange={(e) => {
                          onChange(Number(e.target.value))
                        }}
                        {...rest}
                      />
                    )}
                  />
                </React.Fragment>
              ))}
          </div>
        </div>
      </ValidatedToolSection>
      <Score form={form} validatedTool={validatedTool} response={response} />

      <ValidatedToolSection title="Summary">
        <FormField
          control={form.control}
          name="summary"
          render={({ field: { value, ...rest } }) => (
            <RichTextEditor
              defaultValue={value ?? ''}
              placeholder="Add summary…"
              contentClassName="max-w-(--container-prose)"
              {...rest}
            />
          )}
        />
      </ValidatedToolSection>
      {!response && (
        <div>
          <FormSubmitButton control={form.control}>Submit</FormSubmitButton>
        </div>
      )}
    </ValidatedToolInnerForm>
  )
}

function Score({
  form,
  validatedTool: _validatedTool,
  response: _response,
}: {
  form: UseFormReturn<any>
  validatedTool: ValidatedTool
  response?: ValidatedToolResponse
}): JSX.Element | null {
  const valuesByLinkId = form.watch()

  const performance = Object.entries(valuesByLinkId).reduce(
    (acc, [key, value]) => {
      if (
        key.startsWith('occupational-performance-problems') &&
        key.endsWith('-performance') &&
        typeof value === 'number'
      ) {
        return {
          total: acc.total + value,
          count: acc.count + 1,
        }
      }
      return acc
    },
    { total: 0, count: 0 }
  )
  const satisfaction = Object.entries(valuesByLinkId).reduce(
    (acc, [key, value]) => {
      if (
        key.startsWith('occupational-performance-problems') &&
        key.endsWith('-satisfaction') &&
        typeof value === 'number'
      ) {
        return {
          total: acc.total + value,
          count: acc.count + 1,
        }
      }
      return acc
    },
    { total: 0, count: 0 }
  )
  const performanceScore = performance.total / performance.count
  const satisfactionScore = satisfaction.total / satisfaction.count

  return (
    <div>
      <p>
        <b>Performance</b>:{' '}
        {performance.total === 0 ? '--' : performanceScore.toFixed(2)}
      </p>
      <p>
        <b>Satisfaction</b>:{' '}
        {satisfaction.total === 0 ? '--' : satisfactionScore.toFixed(2)}
      </p>
    </div>
  )
}

function ThingWithImportance({
  control,
  linkId,
  index,
}: {
  control: UseFormReturn<any>['control']
  linkId: string
  index: number
}): JSX.Element {
  return (
    <>
      <FormField
        control={control}
        name={`${linkId}-${index}` as any}
        render={({ field }) => (
          <RichTextEditor
            variant="underline"
            placeholder={`Thing ${index + 1}…`}
            contentClassName="max-w-(--container-prose)"
            className="col-span-2"
            {...field}
          />
        )}
      />
      <FormField
        control={control}
        name={`${linkId}-${index}-importance` as any}
        render={({ field: { onChange, ...rest } }) => (
          <Input
            type="number"
            placeholder="Importance (1-10)"
            className="col-span-1"
            onChange={(e) => {
              onChange(Number(e.target.value))
            }}
            {...rest}
          />
        )}
      />
    </>
  )
}
